import { useCallback, useEffect, useMemo, useState } from "react";
import {
	ChevronDown,
	ChevronUp,
	Dumbbell,
	Edit,
	Filter,
	Loader2,
	Plus,
	Search,
	Target,
	Trash2,
	X,
	Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI } from "../../lib/utils";
import {
	formatValueByUnit,
	getInputPropsForUnit,
	parseValueByUnit,
} from "../../lib/coachUtils";
import {
	DEFAULT_SCORE_COLUMNS_TEXT,
	MIN_SCORE_COLUMNS,
	PERFORMANCE_UNITS,
	buildReferenceUpdate,
	classesOfGender,
	scoreKey,
	scoreLabelOf,
	filterLibraryItems,
	getReferenceValue,
	moveItem,
	normalizeScoreColumns,
	parseScoreColumnsInput,
	toLibraryItems,
} from "../../lib/performanceModelUtils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { WeightClassesDialog } from "./WeightClassesDialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

const EMPTY_DOMAIN_FORM = {
	name: "",
	description: "",
	color: "#00D68F",
	order: 0,
	scoring_columns_text: DEFAULT_SCORE_COLUMNS_TEXT,
};

const CONDITIONING_TYPE_LABELS = {
	amrap: "AMRAP",
	for_time: "For Time",
	emom: "EMOM",
	free: "Custom",
};

const errorText = (error) => error?.detail || error?.message || "";

// Il coefficiente sul peso (D6b): un parametro in piu', acceso dal modello.
const COEFFICIENT_OPTIONS = [
	{ value: "none", labelKey: "pmCoefficientNone" },
	{ value: "sinclair", labelKey: "coefSinclair" },
	{ value: "ipf_gl", labelKey: "coefIpfGl" },
];
const EQUIPMENT_OPTIONS = [
	{ value: "classic", labelKey: "ipfClassic" },
	{ value: "equipped", labelKey: "ipfEquipped" },
];
// Le alzate di gara: l'IPF GL esiste per la panca e per il totale delle tre.
const IPF_LIFT_OPTIONS = [
	{ value: "", labelKey: "pmIpfLiftNone" },
	{ value: "squat", labelKey: "pmIpfSquat" },
	{ value: "bench", labelKey: "pmIpfBench" },
	{ value: "deadlift", labelKey: "pmIpfDeadlift" },
];
const chipClass = (active) =>
	`h-7 rounded-sm border px-2 text-xs transition-colors ${
		active
			? "border-volt/60 bg-volt/15 text-volt"
			: "border-border text-muted-foreground hover:border-volt/40"
	}`;

/**
 * L'editor di un modello prestativo: domini, scala, benchmark e tabelle.
 *
 * Era il tab «Profilo Prestativo» di `AdminDashboard.js`, scritto per il solo
 * modello di sistema. Con la sezione «Sport e modelli» lo usano l'admin (modello
 * di sistema, e quelli dei coach per supporto) e i coach (il proprio modello, in
 * scrittura; quello di sistema e quelli del team, in lettura). Una sola
 * implementazione: se cambia come si costruisce un modello, cambia per tutti.
 *
 * Parla solo con `/performance-models/{model_id}/...`: i permessi li decide il
 * server (`can_write_model`), `canEdit` serve a non mostrare comandi inutili.
 *
 * Props:
 *  - model: il modello (`model_id`, `sport`, `is_system`, `segmentation`,
 *    `bodyweight_coefficient`; da `GET /performance-models/{id}` anche
 *    `weight_classes`, `can_edit_classes`, `sport_id`, `sport_label`)
 *  - canEdit: mostra i comandi di modifica
 *  - sportIsShooting: offre le tabelle per division (D4 del modulo tiro); agli
 *    altri sport le tabelle per categoria di peso e il Sinclair (D6b), che
 *    restano spenti finche' il modello non li chiede
 *  - onModelChanged(model): dopo un cambio del modello stesso (segmentazione)
 *  - onContentChanged(): dopo ogni cambio di domini o esercizi
 *
 * Spec: docs/sport-config/ANALYSIS.md §4.5
 */
export function PerformanceModelEditor({
	model,
	canEdit = false,
	sportIsShooting = false,
	onModelChanged,
	onContentChanged,
}) {
	const { t } = useLanguage();
	const modelId = model?.model_id;
	const base = modelId
		? `/performance-models/${encodeURIComponent(modelId)}`
		: null;
	const isSystemModel = !!model?.is_system;
	const segmentation = model?.segmentation || null;
	const coefficient = model?.bodyweight_coefficient || null;
	const equipment = model?.coefficient_equipment || "classic";

	const [domains, setDomains] = useState([]);
	const [exercises, setExercises] = useState([]);
	const [loading, setLoading] = useState(true);

	const [isDomainDialogOpen, setIsDomainDialogOpen] = useState(false);
	const [editingDomain, setEditingDomain] = useState(null);
	const [domainForm, setDomainForm] = useState(EMPTY_DOMAIN_FORM);
	const [isSavingDomain, setIsSavingDomain] = useState(false);
	// { [domainId]: "add" | `remove:${score}` } -> operazione scala in corso
	const [pendingScoreColumn, setPendingScoreColumn] = useState({});
	const [newScoreColumn, setNewScoreColumn] = useState({});

	const [referenceTarget, setReferenceTarget] = useState({ kind: "male" });
	const [divisions, setDivisions] = useState([]);
	// Le categorie di peso sono dello sport (D6b): arrivano col modello e si
	// aggiornano qui quando chi governa lo sport le cambia.
	const [classes, setClasses] = useState(() =>
		Array.isArray(model?.weight_classes) ? model.weight_classes : [],
	);
	const [classesOpen, setClassesOpen] = useState(false);

	const [isAddExerciseOpen, setIsAddExerciseOpen] = useState(false);
	const [addExerciseDomainId, setAddExerciseDomainId] = useState(null);
	const [library, setLibrary] = useState(null);
	const [librarySearch, setLibrarySearch] = useState("");
	const [libraryUnit, setLibraryUnit] = useState("kg");
	const [librarySource, setLibrarySource] = useState("all");
	const [libraryCategory, setLibraryCategory] = useState("all");

	const unitLabel = (unit) => {
		const found = PERFORMANCE_UNITS.find((u) => u.value === unit);
		return found ? t(found.labelKey) : unit;
	};

	const refresh = useCallback(async () => {
		if (!base) return;
		const [domainsData, exercisesData] = await Promise.all([
			fetchAPI(`${base}/domains`).catch(() => null),
			fetchAPI(`${base}/exercises`).catch(() => null),
		]);
		if (Array.isArray(domainsData)) setDomains(domainsData);
		if (Array.isArray(exercisesData)) setExercises(exercisesData);
	}, [base]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		refresh().finally(() => {
			if (!cancelled) setLoading(false);
		});
		return () => {
			cancelled = true;
		};
	}, [refresh]);

	// Le division servono solo a chi segmenta per division (il tiro).
	useEffect(() => {
		if (segmentation !== "division" || !model?.sport) {
			setDivisions([]);
			setReferenceTarget((prev) =>
				prev.kind === "division" ? { kind: "male" } : prev,
			);
			return undefined;
		}
		let cancelled = false;
		fetchAPI(
			`/shooting/reference?discipline=${encodeURIComponent(model.sport)}`,
		)
			.then((data) => {
				if (!cancelled)
					setDivisions(Array.isArray(data?.divisions) ? data.divisions : []);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [segmentation, model?.sport]);

	useEffect(() => {
		const next = Array.isArray(model?.weight_classes) ? model.weight_classes : [];
		// Stesso elenco se le chiavi non cambiano: ogni aggiornamento del modello
		// (il coefficiente, la segmentazione) porta un array nuovo, e senza questo
		// la tabella scelta a mano tornava alla prima categoria.
		const keysOf = (list) => list.map((item) => item?.key).join(",");
		setClasses((prev) => (keysOf(prev) === keysOf(next) ? prev : next));
	}, [model?.weight_classes]);

	// La tabella che si apre per un genere: in un modello per categorie la prima
	// categoria, perche' e' li' che si leggono gli atleti; la tabella del genere
	// e' solo la riserva per chi non ha peso, e resta scegliibile dal menu.
	const targetForGender = useCallback(
		(gender) => {
			const first =
				segmentation === "weight_class" ? classesOfGender(classes, gender)[0] : null;
			return first ? { kind: "weight_class", key: first.key, gender } : { kind: gender };
		},
		[segmentation, classes],
	);

	// Fuori dalle categorie, la tabella in modifica torna quella del genere;
	// dentro, si parte da una categoria (anche quando le categorie cambiano).
	useEffect(() => {
		setReferenceTarget((prev) => {
			if (segmentation !== "weight_class") {
				return prev.kind === "weight_class" ? { kind: prev.gender || "male" } : prev;
			}
			if (prev.kind === "male" || prev.kind === "female") return targetForGender(prev.kind);
			if (prev.kind === "weight_class" && !classes.some((item) => item.key === prev.key)) {
				return targetForGender(prev.gender || "male");
			}
			return prev;
		});
	}, [segmentation, classes, targetForGender]);

	const afterChange = async () => {
		await refresh();
		onContentChanged?.();
	};

	const exercisesOf = (domainId) =>
		exercises
			.filter((e) => e.domain_id === domainId)
			.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

	// ── Scala del dominio ──────────────────────────────────────────────────

	const setScorePending = (domainId, token) => {
		setPendingScoreColumn((prev) => {
			if (!token) {
				const { [domainId]: _removed, ...rest } = prev;
				return rest;
			}
			return { ...prev, [domainId]: token };
		});
	};

	const putDomain = (domainId, body) =>
		fetchAPI(`${base}/domains/${encodeURIComponent(domainId)}`, {
			method: "PUT",
			body: JSON.stringify(body),
		});

	const handleAddScoreColumn = async (domain) => {
		const domainId = domain?.domain_id;
		if (!domainId || pendingScoreColumn[domainId]) return;
		const input = (newScoreColumn[domainId] || "").trim();
		if (!input) {
			toast.error(t("pmErrScaleValueRequired"));
			return;
		}
		const score = Number(input.replace(",", "."));
		if (!Number.isFinite(score)) {
			toast.error(t("pmErrNotNumber").replace("{value}", input));
			return;
		}
		const current = normalizeScoreColumns(domain.scoring_columns);
		if (current.some((value) => Number(value) === score)) {
			toast.error(t("pmErrColumnExists").replace("{score}", score));
			return;
		}
		setScorePending(domainId, "add");
		try {
			await putDomain(domainId, {
				scoring_columns: normalizeScoreColumns([...current, score]),
			});
			setNewScoreColumn((prev) => ({ ...prev, [domainId]: "" }));
			toast.success(t("pmColumnAdded").replace("{score}", score));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		} finally {
			setScorePending(domainId, null);
		}
	};

	const handleRemoveScoreColumn = async (domain, scoreToRemove) => {
		const domainId = domain?.domain_id;
		if (!domainId || pendingScoreColumn[domainId]) return;
		const current = normalizeScoreColumns(domain.scoring_columns);
		if (current.length <= MIN_SCORE_COLUMNS) {
			toast.error(t("pmErrMinColumns").replace("{n}", MIN_SCORE_COLUMNS));
			return;
		}
		setScorePending(domainId, `remove:${scoreToRemove}`);
		try {
			await putDomain(domainId, {
				scoring_columns: current.filter(
					(score) => Number(score) !== Number(scoreToRemove),
				),
			});
			toast.success(t("pmColumnRemoved").replace("{score}", scoreToRemove));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		} finally {
			setScorePending(domainId, null);
		}
	};

	// Il nome di un livello: facoltativo, uno per colonna. Si salva l'intera
	// mappa, normalizzata dal server sulla scala del dominio.
	const handleScoreLabel = async (domain, score, text) => {
		const labels = domain?.scoring_labels || {};
		const key = scoreKey(score);
		const next = String(text || "").trim();
		if ((labels[key] || "") === next) return;
		const body = { ...labels };
		if (next) body[key] = next;
		else delete body[key];
		try {
			const updated = await putDomain(domain.domain_id, { scoring_labels: body });
			setDomains((prev) =>
				prev.map((item) =>
					item.domain_id === domain.domain_id ? { ...item, ...updated } : item,
				),
			);
			toast.success(t("pmScoreLabelsSaved"));
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	// ── Domini ─────────────────────────────────────────────────────────────

	const closeDomainDialog = () => {
		setIsDomainDialogOpen(false);
		setEditingDomain(null);
		setDomainForm(EMPTY_DOMAIN_FORM);
	};

	const openNewDomain = () => {
		setEditingDomain(null);
		setDomainForm({ ...EMPTY_DOMAIN_FORM, order: domains.length });
		setIsDomainDialogOpen(true);
	};

	const openEditDomain = (domain) => {
		setEditingDomain(domain);
		setDomainForm({
			name: domain.name,
			description: domain.description || "",
			color: domain.color || "#00D68F",
			order: domain.order || 0,
			scoring_columns_text: normalizeScoreColumns(domain.scoring_columns).join(
				", ",
			),
		});
		setIsDomainDialogOpen(true);
	};

	const handleSaveDomain = async () => {
		if (isSavingDomain) return;
		if (!domainForm.name.trim()) {
			toast.error(t("pmErrDomainName"));
			return;
		}
		const { values, invalid } = parseScoreColumnsInput(
			domainForm.scoring_columns_text,
		);
		if (invalid.length > 0) {
			toast.error(t("pmErrScaleInvalid").replace("{values}", invalid.join(", ")));
			return;
		}
		if (values.length < MIN_SCORE_COLUMNS) {
			toast.error(t("pmErrScaleMin").replace("{n}", MIN_SCORE_COLUMNS));
			return;
		}
		const payload = {
			name: domainForm.name.trim(),
			description: domainForm.description,
			color: domainForm.color,
			scoring_columns: values,
		};
		setIsSavingDomain(true);
		try {
			if (editingDomain) {
				await putDomain(editingDomain.domain_id, {
					...payload,
					order: domainForm.order,
				});
				toast.success(t("pmDomainUpdated"));
			} else {
				await fetchAPI(`${base}/domains`, {
					method: "POST",
					body: JSON.stringify({ ...payload, order: domains.length }),
				});
				toast.success(t("pmDomainCreated"));
			}
			closeDomainDialog();
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		} finally {
			setIsSavingDomain(false);
		}
	};

	const handleDeleteDomain = async (domainId) => {
		if (!window.confirm(t("pmConfirmDeleteDomain"))) return;
		try {
			await fetchAPI(`${base}/domains/${encodeURIComponent(domainId)}`, {
				method: "DELETE",
			});
			toast.success(t("pmDomainDeleted"));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	// ── Esercizi ───────────────────────────────────────────────────────────

	const putExercise = (configId, body) =>
		fetchAPI(`${base}/exercises/${encodeURIComponent(configId)}`, {
			method: "PUT",
			body: JSON.stringify(body),
		});

	const handleUpdateReference = async (exercise, score, rawValue, unit) => {
		if (!exercise?.config_id) return;
		const parsed = parseValueByUnit(rawValue, unit);
		const empty = rawValue === "" || rawValue === null;
		if (!empty && parsed === null) {
			const format = getInputPropsForUnit(unit).placeholder || "";
			toast.error(t("pmErrFormat").replace("{format}", format));
			return;
		}
		// Nessun cambiamento: niente scrittura (il blur scatta anche senza modifiche).
		const before = getReferenceValue(exercise, score, referenceTarget);
		if ((empty && before === "") || (!empty && Number(before) === parsed)) return;
		try {
			await putExercise(
				exercise.config_id,
				buildReferenceUpdate(exercise, score, empty ? null : parsed, referenceTarget),
			);
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const handleUpdateUnit = async (configId, unit) => {
		try {
			await putExercise(configId, { unit });
			toast.success(t("pmUnitUpdated"));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const handleReorder = async (domainId, configId, direction) => {
		const reordered = moveItem(
			exercisesOf(domainId),
			configId,
			direction,
			(item) => item.config_id,
		);
		if (!reordered) return;
		try {
			await Promise.all(
				reordered.map((exercise, index) =>
					putExercise(exercise.config_id, { order: index }),
				),
			);
			toast.success(t("pmOrderUpdated"));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const handleRemoveExercise = async (configId) => {
		try {
			await fetchAPI(`${base}/exercises/${encodeURIComponent(configId)}`, {
				method: "DELETE",
			});
			toast.success(t("pmExerciseRemoved"));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const openAddExercise = async (domainId) => {
		setAddExerciseDomainId(domainId);
		setLibrarySearch("");
		setLibraryUnit("kg");
		setLibrarySource("all");
		setLibraryCategory("all");
		setIsAddExerciseOpen(true);
		if (library) return;
		// La libreria si carica alla prima apertura: chi guarda un modello senza
		// modificarlo non paga due elenchi interi.
		const [exercisesData, conditioningData] = await Promise.all([
			fetchAPI("/exercises").catch(() => []),
			fetchAPI("/conditioning-library").catch(() => []),
		]);
		setLibrary(
			toLibraryItems(
				Array.isArray(exercisesData) ? exercisesData : [],
				Array.isArray(conditioningData) ? conditioningData : [],
			),
		);
	};

	const handleAddExercise = async (item) => {
		if (!addExerciseDomainId) return;
		try {
			await fetchAPI(`${base}/exercises`, {
				method: "POST",
				body: JSON.stringify({
					domain_id: addExerciseDomainId,
					exercise_id: item._sourceId,
					exercise_name: item.name,
					unit: libraryUnit,
					order: exercisesOf(addExerciseDomainId).length,
				}),
			});
			toast.success(t("pmExerciseAdded").replace("{name}", item.name));
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const availableItems = useMemo(
		() =>
			filterLibraryItems(library || [], {
				usedIds: exercises
					.filter((e) => e.domain_id === addExerciseDomainId)
					.map((e) => e.exercise_id),
				search: librarySearch,
				source: librarySource,
				category: libraryCategory,
				globalOnly: isSystemModel,
			}),
		[
			library,
			exercises,
			addExerciseDomainId,
			librarySearch,
			librarySource,
			libraryCategory,
			isSystemModel,
		],
	);

	const categoryOptions = useMemo(() => {
		const categories = new Set();
		(library || []).forEach((item) => {
			if (item.category) categories.add(String(item.category).toLowerCase());
		});
		return Array.from(categories).sort();
	}, [library]);

	// ── Segmentazione del modello ──────────────────────────────────────────

	const handleSegmentationChange = async (value) => {
		try {
			const updated = await fetchAPI(base, {
				method: "PUT",
				body: JSON.stringify({ segmentation: value === "none" ? null : value }),
			});
			toast.success(t("pmSegmentationUpdated"));
			onModelChanged?.(updated);
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const updateModelField = async (body) => {
		try {
			const updated = await fetchAPI(base, {
				method: "PUT",
				body: JSON.stringify(body),
			});
			toast.success(t("pmSinclairUpdated"));
			onModelChanged?.(updated);
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const handleCoefficientChange = (value) =>
		updateModelField({ bodyweight_coefficient: value === "none" ? null : value });

	const handleEquipmentChange = (value) =>
		updateModelField({ coefficient_equipment: value });

	const handleIpfLiftChange = async (exercise, value) => {
		try {
			await fetchAPI(`${base}/exercises/${encodeURIComponent(exercise.config_id)}`, {
				method: "PUT",
				body: JSON.stringify({ ipf_lift: value || null }),
			});
			await afterChange();
		} catch (error) {
			toast.error(`${t("error")}: ${errorText(error)}`);
		}
	};

	const ipfLiftLabel = (value) => {
		const found = IPF_LIFT_OPTIONS.find((option) => option.value === value);
		return found && value ? t(found.labelKey) : "";
	};

	const referenceTabs = [
		{ kind: "male", label: t("pmMale") },
		{ kind: "female", label: t("pmFemale") },
	];
	// Il genere della tabella in modifica: quello del tab, o quello della
	// categoria scelta (le categorie sono per genere).
	const activeGender =
		referenceTarget.kind === "weight_class"
			? referenceTarget.gender || "male"
			: referenceTarget.kind === "female"
				? "female"
				: "male";
	const genderClasses = classesOfGender(classes, activeGender);

	if (!modelId) return null;

	if (loading) {
		return (
			<div className="flex items-center justify-center py-10 text-muted-foreground">
				<Loader2 className="w-5 h-5 animate-spin mr-2" />
				{t("loading")}
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{(canEdit || segmentation || coefficient) && (
				<div className="flex flex-wrap items-center justify-between gap-3">
					{canEdit ? (
						<div className="flex flex-wrap items-center gap-3">
							<div className="flex items-center gap-2">
								<Label className="text-xs text-muted-foreground">
									{t("pmTablesLabel")}
								</Label>
								<Select
									value={segmentation || "none"}
									onValueChange={handleSegmentationChange}
								>
									<SelectTrigger
										className="h-8 w-[220px] text-xs bg-secondary border-transparent rounded-sm"
										aria-label={t("pmTablesLabel")}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="none">{t("pmTablesByGender")}</SelectItem>
										{(sportIsShooting || segmentation === "division") && (
											<SelectItem value="division">
												{t("pmTablesByDivision")}
											</SelectItem>
										)}
										{(!sportIsShooting || segmentation === "weight_class") && (
											<SelectItem value="weight_class">
												{t("pmTablesByWeightClass")}
											</SelectItem>
										)}
									</SelectContent>
								</Select>
							</div>
							{(!sportIsShooting || coefficient) && (
								<div
									className="flex flex-wrap items-center gap-1.5"
									role="group"
									aria-label={t("pmCoefficient")}
									data-testid="pm-coefficient"
								>
									<span className="text-xs text-muted-foreground">
										{t("pmCoefficient")}
									</span>
									{COEFFICIENT_OPTIONS.map((option) => {
										const active = (coefficient || "none") === option.value;
										return (
											<button
												key={option.value}
												type="button"
												aria-pressed={active}
												className={chipClass(active)}
												onClick={() => !active && handleCoefficientChange(option.value)}
											>
												{t(option.labelKey)}
											</button>
										);
									})}
									{coefficient === "ipf_gl" &&
										EQUIPMENT_OPTIONS.map((option) => {
											const active = equipment === option.value;
											return (
												<button
													key={option.value}
													type="button"
													aria-pressed={active}
													className={chipClass(active)}
													onClick={() => !active && handleEquipmentChange(option.value)}
												>
													{t(option.labelKey)}
												</button>
											);
										})}
								</div>
							)}
						</div>
					) : (
						<p className="text-xs text-muted-foreground">
							{[
								segmentation === "division" && t("pmTablesByDivision"),
								segmentation === "weight_class" && t("pmTablesByWeightClass"),
								coefficient === "sinclair" && t("coefSinclair"),
								coefficient === "ipf_gl" &&
									`${t("coefIpfGl")} ${t(equipment === "equipped" ? "ipfEquipped" : "ipfClassic")}`,
							]
								.filter(Boolean)
								.join(" · ")}
						</p>
					)}
					{canEdit && (
						<Button
							onClick={openNewDomain}
							className="bg-volt hover:bg-volt/90 rounded-sm"
						>
							<Plus className="w-4 h-4 mr-2" />
							{t("pmNewDomain")}
						</Button>
					)}
				</div>
			)}

			{coefficient === "ipf_gl" && (
				<p className="text-[11px] text-muted-foreground" data-testid="pm-ipf-hint">
					{t("pmIpfGlHint")}
				</p>
			)}

			{segmentation === "weight_class" && (
				<div
					className="space-y-1.5 rounded-sm border border-border bg-secondary/30 p-3 text-xs"
					data-testid="pm-weight-classes"
				>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="font-medium text-foreground">{t("pmWeightClassesTitle")}</p>
						{model?.can_edit_classes && model?.sport_id && (
							<Button
								size="sm"
								variant="outline"
								className="h-7 rounded-sm text-xs"
								onClick={() => setClassesOpen(true)}
							>
								{t("pmManageClasses")}
							</Button>
						)}
					</div>
					{classes.length === 0 ? (
						<p className="text-amber-300">
							{model?.can_edit_classes ? t("pmNoClassesCanEdit") : t("pmNoClassesAskAdmin")}
						</p>
					) : (
						referenceTabs.map((tab) => {
							const own = classesOfGender(classes, tab.kind);
							return own.length > 0 ? (
								<p key={tab.kind}>
									<span className="text-muted-foreground">{tab.label}: </span>
									{own.map((item) => item.label).join(" · ")}
								</p>
							) : null;
						})
					)}
					<p className="text-muted-foreground">{t("pmWeightClassHint")}</p>
				</div>
			)}

			{domains.length === 0 ? (
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="flex flex-col items-center justify-center py-12 text-center">
						<Target className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
						<p className="font-heading text-lg mb-2">{t("pmNoDomains")}</p>
						<p className="text-sm text-muted-foreground mb-4">
							{t("pmNoDomainsHint")}
						</p>
						{canEdit && (
							<Button
								onClick={openNewDomain}
								className="bg-volt hover:bg-volt/90 rounded-sm"
							>
								<Plus className="w-4 h-4 mr-2" />
								{t("pmCreateFirstDomain")}
							</Button>
						)}
					</CardContent>
				</Card>
			) : (
				domains.map((domain) => {
					const domainExercises = exercisesOf(domain.domain_id);
					const scoreColumns = normalizeScoreColumns(domain.scoring_columns);
					const pending = pendingScoreColumn[domain.domain_id] || null;
					const canRemove = scoreColumns.length > MIN_SCORE_COLUMNS;
					return (
						<Card
							key={domain.domain_id}
							className="bg-card border-border rounded-sm"
							data-testid={`pm-domain-${domain.domain_id}`}
						>
							<CardHeader className="pb-3">
								<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
									<div className="flex items-center gap-3 min-w-0">
										<div
											className="w-3 h-8 rounded-sm shrink-0"
											style={{ backgroundColor: domain.color || "#00D68F" }}
										/>
										<div className="min-w-0">
											<CardTitle className="font-heading text-lg break-words">
												{domain.name}
											</CardTitle>
											{domain.description && (
												<p
													className="text-sm text-muted-foreground break-words line-clamp-2"
													title={domain.description}
												>
													{domain.description}
												</p>
											)}
										</div>
									</div>
									<div className="flex flex-wrap items-center gap-2 sm:justify-end">
										<Badge className="bg-secondary text-muted-foreground border-0">
											{t("pmExercisesCount").replace(
												"{count}",
												domainExercises.length,
											)}
										</Badge>
										<Badge className="bg-volt/10 text-volt border-0">
											{t("pmScale")}: {scoreColumns.join(" · ")}
										</Badge>
										{canEdit && (
											<>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm"
													title={t("pmEditDomainHint")}
													onClick={() => openEditDomain(domain)}
												>
													<Edit className="w-4 h-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm hover:text-red-500"
													title={t("pmDeleteDomain")}
													onClick={() => handleDeleteDomain(domain.domain_id)}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</>
										)}
									</div>
								</div>
								{canEdit && (
									<div className="mt-3 rounded-sm border border-border/60 bg-secondary/30 p-3">
										<div className="flex flex-wrap items-center justify-between gap-2">
											<p className="text-[11px] uppercase tracking-wider text-muted-foreground">
												{t("pmScoreScale")}
											</p>
											<p className="text-[11px] text-muted-foreground">
												{t("pmScaleMinColumns").replace("{n}", MIN_SCORE_COLUMNS)}
											</p>
										</div>
										<div className="mt-2 flex flex-wrap items-center gap-2">
											{scoreColumns.map((score) => {
												const isRemoving = pending === `remove:${score}`;
												return (
													<Badge
														key={`${domain.domain_id}_score_${score}`}
														className="bg-secondary border-border text-foreground gap-1"
													>
														<span>{score}</span>
														<button
															type="button"
															disabled={!!pending || !canRemove}
															onClick={() =>
																handleRemoveScoreColumn(domain, score)
															}
															className="text-muted-foreground hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
															title={
																canRemove
																	? t("pmRemoveColumn").replace("{score}", score)
																	: t("pmRemoveColumnBlocked").replace(
																			"{n}",
																			MIN_SCORE_COLUMNS,
																		)
															}
														>
															{isRemoving ? (
																<Loader2 className="w-3 h-3 animate-spin" />
															) : (
																<X className="w-3 h-3" />
															)}
														</button>
													</Badge>
												);
											})}
											<div className="flex items-center gap-1">
												<Input
													value={newScoreColumn[domain.domain_id] || ""}
													disabled={!!pending}
													onChange={(e) =>
														setNewScoreColumn((prev) => ({
															...prev,
															[domain.domain_id]: e.target.value,
														}))
													}
													onKeyDown={(e) => {
														if (e.key === "Enter") {
															e.preventDefault();
															handleAddScoreColumn(domain);
														}
													}}
													inputMode="decimal"
													className="h-8 w-20 bg-background border-border rounded-sm"
													placeholder={t("pmScaleAddPlaceholder")}
												/>
												<Button
													size="sm"
													disabled={!!pending}
													className="h-8 rounded-sm bg-volt hover:bg-volt/90"
													onClick={() => handleAddScoreColumn(domain)}
												>
													{pending === "add" ? (
														<Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
													) : (
														<Plus className="w-3.5 h-3.5 mr-1" />
													)}
													{t("pmAdd")}
												</Button>
											</div>
										</div>
										<p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
											{t("pmScaleHelp1")}{" "}
											<button
												type="button"
												onClick={() => openEditDomain(domain)}
												className="underline underline-offset-2 text-volt hover:text-volt/80"
											>
												{t("pmEditDomain")}
											</button>
											. {t("pmScaleHelp2").replace("{n}", MIN_SCORE_COLUMNS)}
										</p>
										<div className="mt-3 border-t border-border/50 pt-3">
											<p className="text-[11px] uppercase tracking-wider text-muted-foreground">
												{t("pmScoreLabels")}
											</p>
											<p className="mt-0.5 text-[11px] text-muted-foreground">
												{t("pmScoreLabelsHint")}
											</p>
											<div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
												{scoreColumns.map((score) => {
													const current = scoreLabelOf(domain.scoring_labels, score) || "";
													return (
														<label
															key={`${domain.domain_id}_label_${score}`}
															className="flex items-center gap-2"
														>
															<span className="w-8 shrink-0 text-right text-xs tabular-nums text-volt">
																{score}
															</span>
															<Input
																key={`${domain.domain_id}_label_${score}_${current}`}
																defaultValue={current}
																maxLength={60}
																placeholder={t("pmScoreLabelPlaceholder")}
																aria-label={`${t("pmScoreLabels")} ${score}`}
																onBlur={(event) =>
																	handleScoreLabel(domain, score, event.target.value)
																}
																className="h-8 text-xs bg-background border-border rounded-sm"
															/>
														</label>
													);
												})}
											</div>
										</div>
									</div>
								)}
								{!canEdit && Object.keys(domain.scoring_labels || {}).length > 0 && (
									<p className="mt-2 text-[11px] text-muted-foreground" data-testid="pm-score-labels">
										{scoreColumns
											.filter((score) => scoreLabelOf(domain.scoring_labels, score))
											.map((score) => `${score} ${scoreLabelOf(domain.scoring_labels, score)}`)
											.join(" · ")}
									</p>
								)}
							</CardHeader>
							<CardContent>
								<div className="flex flex-wrap items-center justify-between gap-2 mb-3">
									<p className="text-xs text-muted-foreground uppercase tracking-widest">
										{t("pmReferences")}
									</p>
									<div className="flex flex-wrap items-center gap-2">
										<div className="flex items-center gap-1 border border-border rounded-sm p-1 bg-background/70">
											{referenceTabs.map((tab) => {
												const active =
													referenceTarget.kind === tab.kind ||
													(referenceTarget.kind === "weight_class" &&
														activeGender === tab.kind);
												return (
													<Button
														key={tab.kind}
														type="button"
														size="sm"
														variant={active ? "default" : "ghost"}
														className={`h-7 rounded-sm ${
															active ? "bg-volt hover:bg-volt/90 text-white" : ""
														}`}
														onClick={() => setReferenceTarget(targetForGender(tab.kind))}
													>
														{tab.label}
													</Button>
												);
											})}
										</div>
										{segmentation === "division" && divisions.length > 0 && (
											<Select
												value={
													referenceTarget.kind === "division"
														? referenceTarget.key
														: "__none__"
												}
												onValueChange={(value) =>
													setReferenceTarget(
														value === "__none__"
															? { kind: "male" }
															: { kind: "division", key: value },
													)
												}
											>
												<SelectTrigger className="h-8 w-[170px] text-xs bg-background border-border rounded-sm">
													<SelectValue placeholder={t("pmDivision")} />
												</SelectTrigger>
												<SelectContent className="bg-card border-border">
													<SelectItem value="__none__">{t("pmDivision")}</SelectItem>
													{divisions.map((division) => (
														<SelectItem key={division.value} value={division.value}>
															{division.label || division.value}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
										{segmentation === "weight_class" && genderClasses.length > 0 && (
											<Select
												value={
													referenceTarget.kind === "weight_class"
														? referenceTarget.key
														: "__none__"
												}
												onValueChange={(value) =>
													setReferenceTarget(
														value === "__none__"
															? { kind: activeGender }
															: { kind: "weight_class", key: value, gender: activeGender },
													)
												}
											>
												<SelectTrigger
													className="h-8 w-[170px] text-xs bg-background border-border rounded-sm"
													aria-label={t("pmWeightClass")}
												>
													<SelectValue placeholder={t("pmWeightClass")} />
												</SelectTrigger>
												<SelectContent className="bg-card border-border">
													<SelectItem value="__none__">{t("pmGenderTable")}</SelectItem>
													{genderClasses.map((item) => (
														<SelectItem key={item.key} value={item.key}>
															{item.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									</div>
								</div>
								{referenceTarget.kind === "division" && (
									<p className="mb-2 text-[11px] text-muted-foreground">
										{t("pmDivisionEmptyHint")}
									</p>
								)}
								{referenceTarget.kind === "weight_class" && (
									<p className="mb-2 text-[11px] text-muted-foreground">
										{t("pmWeightClassEmptyHint")}
									</p>
								)}
								{segmentation === "weight_class" &&
									(referenceTarget.kind === "male" ||
										referenceTarget.kind === "female") && (
										<p className="mb-2 text-[11px] text-muted-foreground">
											{t("pmGenderTableHint")}
										</p>
									)}
								{domainExercises.length > 0 ? (
									<div className="overflow-x-auto mb-4 border border-border rounded-sm">
										{/* Compatta per stare in pagina su un computer con dieci
										    livelli; sul telefono scorre, col nome dell'esercizio
										    fermo a sinistra. L'unita' sta sotto il nome: una colonna
										    sua bastava a far scorrere tutto. */}
										<table className="w-full min-w-[600px] text-sm">
											<thead>
												<tr className="bg-secondary/60 border-b border-border">
													<th className="sticky left-0 z-10 bg-secondary text-left px-2 py-2 font-medium">
														{t("pmColExercise")}
													</th>
													{scoreColumns.map((score) => (
														<th
															key={`${domain.domain_id}_head_${score}`}
															className="text-center px-0.5 py-2 font-medium text-volt"
														>
															{score}
														</th>
													))}
													{canEdit && (
														<th className="px-1 py-2 font-medium">
															<span className="sr-only">{t("pmColActions")}</span>
														</th>
													)}
												</tr>
											</thead>
											<tbody>
												{domainExercises.map((ex, index) => (
													<tr
														key={ex.config_id}
														className="border-b border-border/60 last:border-b-0"
													>
														<td className="sticky left-0 z-10 bg-card px-2 py-2 min-w-[8.5rem] max-w-[11rem]">
															<div className="flex items-center gap-1.5">
																<span className="text-[11px] tabular-nums text-muted-foreground">
																	{index + 1}
																</span>
																{String(ex.exercise_id || "").startsWith("cond_") ? (
																	<Zap className="w-3.5 h-3.5 shrink-0 text-yellow-400" />
																) : (
																	<Dumbbell className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
																)}
																<span className="font-medium leading-tight">{ex.exercise_name}</span>
															</div>
															{coefficient === "ipf_gl" && ex.unit === "kg" && (
																canEdit ? (
																	<select
																		aria-label={t("pmIpfLift")}
																		value={ex.ipf_lift || ""}
																		onChange={(event) =>
																			handleIpfLiftChange(ex, event.target.value)
																		}
																		className="mt-1 h-7 rounded-sm border border-border bg-background px-1.5 text-xs"
																	>
																		{IPF_LIFT_OPTIONS.map((option) => (
																			<option key={option.value || "none"} value={option.value}>
																				{t(option.labelKey)}
																			</option>
																		))}
																	</select>
																) : ex.ipf_lift ? (
																	<p className="mt-0.5 text-[11px] text-muted-foreground">
																		{ipfLiftLabel(ex.ipf_lift)}
																	</p>
																) : null
															)}
														
															{canEdit ? (
																<Select
																	value={ex.unit}
																	onValueChange={(value) =>
																		handleUpdateUnit(ex.config_id, value)
																	}
																>
																	<SelectTrigger
																		aria-label={t("pmColUnit")}
																		className="mt-1 w-[110px] h-7 text-[11px] bg-background border-border rounded-sm"
																	>
																		<SelectValue />
																	</SelectTrigger>
																	<SelectContent className="bg-card border-border">
																		{PERFORMANCE_UNITS.map((unit) => (
																			<SelectItem key={unit.value} value={unit.value}>
																				{t(unit.labelKey)}
																			</SelectItem>
																		))}
																	</SelectContent>
																</Select>
															) : (
																<p className="mt-0.5 text-[11px] text-muted-foreground">
																	{unitLabel(ex.unit)}
																</p>
															)}
														</td>
														{scoreColumns.map((score) => {
															const refValue = getReferenceValue(
																ex,
																score,
																referenceTarget,
															);
															const displayValue = formatValueByUnit(refValue, ex.unit);
															const targetKey =
																referenceTarget.kind === "division"
																	? `division_${referenceTarget.key}`
																	: referenceTarget.kind === "weight_class"
																		? `wc_${referenceTarget.key}`
																		: referenceTarget.kind;
															return (
																<td key={`${ex.config_id}_${score}`} className="px-0.5 py-2 text-center">
																	{canEdit ? (
																		<Input
																			{...getInputPropsForUnit(ex.unit)}
																			// Il valore nella chiave: `defaultValue` vale solo alla
																			// nascita, e senza la cella restava vuota se gli esercizi
																			// arrivavano o cambiavano dopo.
																			key={`${ex.config_id}_${score}_${targetKey}_${displayValue}`}
																			defaultValue={displayValue}
																			onBlur={(event) =>
																				handleUpdateReference(
																					ex,
																					score,
																					event.target.value,
																					ex.unit,
																				)
																			}
																			// Larga quanto un valore e senza le frecce del campo numerico:
																		// in una cella stretta coprivano il numero.
																		className="mx-auto h-8 w-12 min-w-[3rem] px-1 text-center tabular-nums bg-background border-border rounded-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
																		/>
																	) : (
																		<span className="tabular-nums">
																			{displayValue || "–"}
																		</span>
																	)}
																</td>
															);
														})}
														{canEdit && (
															<td className="px-1 py-2">
																<div className="flex items-center">
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm"
																		title={t("pmMoveUp")}
																		onClick={() =>
																			handleReorder(domain.domain_id, ex.config_id, "up")
																		}
																		disabled={index === 0}
																	>
																		<ChevronUp className="w-3.5 h-3.5" />
																	</Button>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm"
																		title={t("pmMoveDown")}
																		onClick={() =>
																			handleReorder(domain.domain_id, ex.config_id, "down")
																		}
																		disabled={index === domainExercises.length - 1}
																	>
																		<ChevronDown className="w-3.5 h-3.5" />
																	</Button>
																	<Button
																		variant="ghost"
																		size="icon"
																		className="h-7 w-7 rounded-sm hover:text-red-500"
																		title={t("pmRemoveExercise")}
																		onClick={() => handleRemoveExercise(ex.config_id)}
																	>
																		<Trash2 className="w-3.5 h-3.5" />
																	</Button>
																</div>
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								) : (
									<div className="text-center py-6 text-muted-foreground mb-4">
										<Dumbbell className="w-8 h-8 mx-auto mb-2 opacity-50" />
										<p className="text-sm">{t("pmNoExercises")}</p>
										{canEdit && <p className="text-xs">{t("pmNoExercisesHint")}</p>}
									</div>
								)}
								{canEdit && (
									<Button
										variant="outline"
										size="sm"
										className="rounded-sm w-full"
										onClick={() => openAddExercise(domain.domain_id)}
									>
										<Plus className="w-4 h-4 mr-2" />
										{t("pmAddExercise")}
									</Button>
								)}
							</CardContent>
						</Card>
					);
				})
			)}

			{/* Dominio: crea / modifica */}
			<Dialog
				open={isDomainDialogOpen}
				onOpenChange={(open) => {
					if (!open && isSavingDomain) return;
					if (!open) closeDomainDialog();
					else setIsDomainDialogOpen(true);
				}}
			>
				<DialogContent className="bg-card border-border max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle>
							{editingDomain ? t("pmDomainDialogEdit") : t("pmDomainDialogNew")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>{t("pmDomainName")}</Label>
							<Input
								value={domainForm.name}
								onChange={(e) => setDomainForm({ ...domainForm, name: e.target.value })}
								className="bg-secondary border-transparent rounded-sm"
								placeholder={t("pmDomainNamePlaceholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("pmDomainDescription")}</Label>
							{/* Su piu' righe: si legge nella guida «Come si leggono i livelli»
							    del profilo, dove gli a capo restano. */}
							<Textarea
								value={domainForm.description}
								onChange={(e) =>
									setDomainForm({ ...domainForm, description: e.target.value })
								}
								rows={4}
								className="bg-secondary border-transparent rounded-sm text-sm"
								placeholder={t("pmDomainDescriptionPlaceholder")}
							/>
							<p className="text-[11px] text-muted-foreground">{t("pmDomainDescriptionHint")}</p>
						</div>
						<div className="space-y-2">
							<Label>{t("pmDomainColor")}</Label>
							<div className="flex items-center gap-3">
								<input
									type="color"
									value={domainForm.color}
									onChange={(e) => setDomainForm({ ...domainForm, color: e.target.value })}
									className="w-10 h-10 rounded-sm cursor-pointer border-0 bg-transparent"
								/>
								<Input
									value={domainForm.color}
									onChange={(e) => setDomainForm({ ...domainForm, color: e.target.value })}
									className="bg-secondary border-transparent rounded-sm flex-1"
									placeholder="#00D68F"
								/>
							</div>
						</div>
						<div className="space-y-2">
							<Label>{t("pmDomainScale")}</Label>
							<Input
								value={domainForm.scoring_columns_text}
								onChange={(e) =>
									setDomainForm({ ...domainForm, scoring_columns_text: e.target.value })
								}
								className="bg-secondary border-transparent rounded-sm font-mono text-sm"
								placeholder={DEFAULT_SCORE_COLUMNS_TEXT}
							/>
							<p className="text-xs text-muted-foreground">
								{t("pmDomainScaleHelp").replace("{n}", MIN_SCORE_COLUMNS)}
							</p>
							{editingDomain && (
								<p className="text-xs text-amber-400/90">
									{t("pmDomainScaleEditWarning")}
								</p>
							)}
						</div>
						<Button
							onClick={handleSaveDomain}
							disabled={isSavingDomain || !domainForm.name.trim()}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
						>
							{isSavingDomain ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									{t("pmSaving")}
								</>
							) : editingDomain ? (
								t("pmSaveChanges")
							) : (
								t("pmCreateDomain")
							)}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Aggiungi esercizio o conditioning al dominio */}
			<Dialog
				open={isAddExerciseOpen}
				onOpenChange={(open) => {
					setIsAddExerciseOpen(open);
					if (!open) setAddExerciseDomainId(null);
				}}
			>
				<DialogContent className="bg-card border-border max-w-lg">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle>{t("addExerciseToDomainTitle")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>{t("unitOfMeasure")}</Label>
							<Select value={libraryUnit} onValueChange={setLibraryUnit}>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									{PERFORMANCE_UNITS.map((unit) => (
										<SelectItem key={unit.value} value={unit.value}>
											{t(unit.labelKey)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="flex gap-2">
							{[
								{ value: "all", label: t("sourceAll") },
								{ value: "exercise", label: t("sourceExercises") },
								{ value: "conditioning", label: "Conditioning" },
							].map((source) => (
								<button
									key={source.value}
									type="button"
									onClick={() => setLibrarySource(source.value)}
									className={`px-3 py-1.5 rounded-sm text-xs font-heading uppercase tracking-wider transition-colors ${
										librarySource === source.value
											? "bg-volt/20 text-volt border border-volt/30"
											: "bg-secondary text-muted-foreground border border-transparent hover:border-border"
									}`}
								>
									{source.value === "conditioning" && (
										<Zap className="w-3 h-3 inline mr-1" />
									)}
									{source.value === "exercise" && (
										<Dumbbell className="w-3 h-3 inline mr-1" />
									)}
									{source.label}
								</button>
							))}
						</div>

						{isSystemModel && (
							<p className="text-xs text-muted-foreground">
								{t("ppSystemModelGlobalOnly")}
							</p>
						)}

						<div className="flex gap-2 items-center">
							<Filter className="w-4 h-4 text-muted-foreground shrink-0" />
							<Select value={libraryCategory} onValueChange={setLibraryCategory}>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8 text-xs">
									<SelectValue placeholder={t("pmCategory")} />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="all">{t("pmAllCategories")}</SelectItem>
									{categoryOptions.map((category) => (
										<SelectItem key={category} value={category}>
											<span className="capitalize">{category}</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
							<Input
								value={librarySearch}
								onChange={(e) => setLibrarySearch(e.target.value)}
								placeholder={t("searchExerciseOrConditioning")}
								className="pl-10 bg-secondary border-transparent rounded-sm"
							/>
						</div>

						{library === null ? (
							<div className="flex items-center justify-center py-8 text-muted-foreground">
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
								{t("loading")}
							</div>
						) : (
							<>
								<p className="text-xs text-muted-foreground">
									{availableItems.length} {t("availableItemsSuffix")}
									{librarySource !== "all" || libraryCategory !== "all" || librarySearch
										? ` ${t("filteredSuffix")}`
										: ""}
								</p>
								<ScrollArea className="h-[300px]">
									<div className="space-y-1">
										{availableItems.map((item) => (
											<button
												key={item._sourceId}
												type="button"
												onClick={() => handleAddExercise(item)}
												className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-secondary text-left transition-colors"
											>
												<div className="flex items-center gap-3">
													{item._sourceType === "conditioning" ? (
														<Zap className="w-4 h-4 text-yellow-400" />
													) : (
														<Dumbbell className="w-4 h-4 text-muted-foreground" />
													)}
													<div>
														<p className="text-sm font-medium">{item.name}</p>
														<div className="flex items-center gap-2 mt-0.5">
															<span className="text-xs text-muted-foreground capitalize">
																{item.category}
															</span>
															<Badge
																className={`border-0 text-[10px] px-1.5 py-0 ${
																	item._libraryScope === "global"
																		? "bg-amber-500/20 text-amber-300"
																		: "bg-emerald-500/20 text-emerald-300"
																}`}
															>
																{item._libraryScope === "global"
																	? t("libraryScopeGlobalSingle")
																	: t("libraryScopePersonal")}
															</Badge>
															{item._sourceType === "conditioning" &&
																item.conditioningType && (
																	<Badge className="bg-yellow-500/20 text-yellow-400 border-0 text-[10px] px-1.5 py-0">
																		{CONDITIONING_TYPE_LABELS[item.conditioningType] ||
																			item.conditioningType}
																	</Badge>
																)}
														</div>
													</div>
												</div>
												<Plus className="w-4 h-4 text-volt" />
											</button>
										))}
										{availableItems.length === 0 && (
											<p className="text-sm text-muted-foreground text-center py-8">
												{librarySearch ||
												librarySource !== "all" ||
												libraryCategory !== "all"
													? t("noResultsForSelectedFilters")
													: t("allExercisesAlreadyAddedToDomain")}
											</p>
										)}
									</div>
								</ScrollArea>
							</>
						)}
					</div>
				</DialogContent>
			</Dialog>

			<WeightClassesDialog
				open={classesOpen}
				onOpenChange={setClassesOpen}
				sportId={model?.sport_id}
				sportLabel={model?.sport_label || model?.sport}
				classes={classes}
				onSaved={(next) => {
					setClasses(next);
					onModelChanged?.({ weight_classes: next });
				}}
			/>
		</div>
	);
}
